import type { InjectionKey, Ref, RenderFunction } from 'vue';
import { tryOnBeforeUnmount } from '@vueuse/core';
import { defineComponent, inject, onMounted, provide, ref, watch } from 'vue';

/**
 * Creates a teleport system that allows dynamic slot rendering across the component tree.
 *
 * As opposed to the built-in vue `Teleport`, where the teleport target must be rendered within the same tick,
 * the `Teleport` component returned by this function allows you to render the teleported content at any point in time
 * and anywhere that is below the provide/inject point of this context.
 *
 *
 * @param {string} [name] Optional name used for internal component naming.
 * @return {[Function, import("vue").Component, import("vue").Component]} A tuple containing three elements:
 *    1. `provideTeleport`: A function that must be called in a top level component to set up the provide/inject mechanism
 *    2. `Render`: A Vue component to render the registered teleport slots.
 *    3. `Teleport`: A Vue component to register and manage the teleport slots dynamically.
 */
export function createTeleport(name?: string) {
	const key = Symbol(name) as InjectionKey<Ref<RenderFunction[]>>;

	function provideTeleport() {
		const slots = ref<RenderFunction[]>([]);

		provide(key, slots);
	}

	function registerSlot(render: RenderFunction) {
		const slots = inject(key);

		if (!slots) {
			return;
		}

		slots.value.push(render);

		return unregister;

		function unregister() {
			if (!slots) {
				return;
			}

			slots.value = slots.value.filter((slot) => slot !== render);
		}
	}

	const Render = defineComponent({
		name: name ? `${name}Render` : undefined,
		setup() {
			const slots = inject(key) as Ref<RenderFunction[]>;

			if (!slots) {
				return;
			}

			return function () {
				return slots.value.filter(Boolean).map((slot) => slot());
			};
		},
	});

	const Teleport = defineComponent({
		name: name ? `${name}Teleport` : undefined,
		props: {
			disabled: {
				type: Boolean,
			},
		},
		setup(props, { slots }) {
			if (slots.default) {
				let unregister: (() => void) | undefined;

				watch(() => props.disabled, update);
				onMounted(update);

				tryOnBeforeUnmount(() => unregister?.());

				function update() {
					if (props.disabled) {
						unregister?.();
					}
					else {
						unregister = registerSlot(slots.default!);
					}
				}
			}

			return function () {
				return props.disabled ? slots.default?.() : null;
			};
		},
	});

	return [provideTeleport, Render, Teleport] as const;
}
